import { getCSRCookieData } from '@/app/api/CSR/getCSRCookieData'
import { COOKIE_NAME_TO_ACTIVE_NEW_LOGIN } from '@/utils/auth/constants'
import isSSR from '@/utils/isSSR'
import {
  ApolloLink,
  Observable,
  type FetchResult,
  type NextLink,
  type Operation,
} from '@apollo/client'

interface GQLErrorExtensions {
  code?: string
  [key: string]: unknown
}

interface Subscription {
  closed: boolean
  unsubscribe(): void
}

const MAX_REFRESH_RETRIES = 1

const retries = new WeakMap<Operation, number>()

let refreshInFlight: Promise<boolean> | null = null

const getRetryCount = (op: Operation) => retries.get(op) ?? 0

const incRetryCount = (op: Operation) => {
  const n = getRetryCount(op) + 1
  retries.set(op, n)
  return n
}

const resetRetryCount = (op: Operation) => {
  retries.delete(op)
}

const isNewAuthMethodsEnabled = (): boolean => {
  if (isSSR()) return false
  const cookieValue = getCSRCookieData(COOKIE_NAME_TO_ACTIVE_NEW_LOGIN)
  const numValue = Number(cookieValue)

  return numValue >= 1
}

async function refreshTokens(): Promise<boolean> {
  if (isSSR()) return false
  refreshInFlight =
    refreshInFlight ??
    fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null
      })
  return refreshInFlight
}

async function refreshWithLimit(op: Operation): Promise<boolean> {
  let attempts = getRetryCount(op)
  while (attempts < MAX_REFRESH_RETRIES) {
    attempts = incRetryCount(op)
    const ok = await refreshTokens()
    if (ok) return true
  }
  return false
}

const hasUnauthenticatedGQLError = (result: FetchResult) =>
  !!result.errors?.some(
    (e) =>
      (e.extensions as GQLErrorExtensions)?.code === 'UNAUTHENTICATED' ||
      /UNAUTHENTICATED|UNAUTHORIZED/i.test(e.message ?? '')
  )

function updateAuthHeader(operation: Operation) {
  const token =
    getCSRCookieData('_IL-auth-token') || localStorage.getItem('token') || ''
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      token,
    },
  }))
}

export const refreshOnUnauthenticatedLink = new ApolloLink(
  (operation: Operation, forward: NextLink) => {
    if (!forward) return null as unknown as Observable<FetchResult>
    if (isSSR()) return forward(operation) // No refresh in SSR

    if (!isNewAuthMethodsEnabled()) {
      return forward(operation)
    }

    return new Observable<FetchResult>((observer) => {
      let sub: Subscription | null = null

      const reExecute = () => {
        updateAuthHeader(operation)
        sub = forward(operation).subscribe({
          next: (res) => observer.next(res),
          error: (err) => observer.error(err),
          complete: () => {
            resetRetryCount(operation)
            observer.complete()
          },
        })
      }

      sub = forward(operation).subscribe({
        next: (result) => {
          if (!hasUnauthenticatedGQLError(result)) {
            observer.next(result)
            resetRetryCount(operation)
            observer.complete()
            return
          }
          refreshWithLimit(operation)
            .then((ok) => {
              if (!ok) {
                observer.error(new Error('UNAUTHENTICATED (refresh failed)'))
                return
              }
              reExecute()
            })
            .catch((e) => {
              observer.error(e)
            })
        },

        error: (err: Error | unknown) => {
          const status =
            (err as any)?.networkError?.statusCode ??
            (err as any)?.networkError?.status ??
            (err as any)?.statusCode
          if (status !== 401 && status !== '401') {
            resetRetryCount(operation)
            observer.error(err)
            return
          }
          refreshWithLimit(operation)
            .then((ok) => {
              if (!ok) {
                observer.error(err)
                return
              }
              reExecute()
            })
            .catch((e) => {
              observer.error(e)
            })
        },
        complete: () => {
          // no-op: we complete when we emit the retry or the valid result
        },
      })

      return () => sub?.unsubscribe()
    })
  }
)
